import { animated, useSpring } from 'react-spring';
import { EMOJI_ICONS } from '@components/Presentation/main/Settings/RatingPollsSettings';
import { type InteractionSettings } from '@components/Presentation/types';
import { Star } from '@mui/icons-material';
import Counter from '@atoms/new/counter';
import { getInitials } from '@services/utils.service';
import AvatarBadge from '@components/AvatarBadge';
import { Tooltip } from '@nextui-org/react';
import { colorPalette } from '@lib/constants';

const GroupProfile = ({ usernames, colorPalette, contribution }: { usernames: string[], colorPalette: string[], contribution: number }) => <div className="flex items-center relative">
  {usernames?.slice(0, 2).map((username: string, index: number) => {
    return (
      <Tooltip
        key={index}
        content={
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded-full flex justify-center items-center text-[10px] font-semibold text-[#212121]"
              style={{ backgroundColor: colorPalette[index % colorPalette.length] }}
            >
              {getInitials(username)}
            </div>
            <span className="text-sm font-normal text-[#212121]">{username}</span>
          </div>
        }

        placement="top"
        showArrow
        classNames={{
          base: ['before:bg-neutral-400 dark:before:bg-white before:bg-white'],
          content: ['py-2 px-4 shadow-xl', 'text-black bg-white'],
        }}
      >
        <div className={'flex flex-row-reverse'}>
          <AvatarBadge name={getInitials(username)} index={index} />
        </div>
      </Tooltip>
    );
  })}

  {(usernames?.length ?? 0) > 2 && (
    <Tooltip
      placement="top"
      showArrow
      classNames={{
        base: ['before:bg-neutral-400 dark:before:bg-white before:bg-white'],
        content: ['py-2 px-4 shadow-xl', 'text-black bg-white max-h-48 overflow-y-auto rounded-md', 'z-[9999999]'],
      }}
      content={
        <div className="flex flex-col gap-2">
          {usernames
            .slice(2)
            .sort((a, b) => a.localeCompare(b))
            .map((username, idx) => {
              const initials = getInitials(username);
              const bgColor = colorPalette[(4 + idx) % colorPalette.length];

              return (
                <div key={idx} className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full flex justify-center items-center text-[10px] font-semibold text-[#212121]"
                    style={{ backgroundColor: bgColor }}
                  >
                    {initials}
                  </div>
                  <span className="text-sm font-normal text-[#212121]">{username}</span>
                </div>
              );
            })}
        </div>
      }
    >
      <div
        className="w-[38px] h-[38px] rounded-full z-50 flex justify-center items-center relative bg-white"

      >
        <div className="text-[#212121]  text-sm font-semibold font-['Inter'] leading-tight">
          {contribution - 2}+
        </div>
      </div>
    </Tooltip>
  )}
</div>;
const Bar = (props: {
  origin: 'EDIT_PRESENTATION' | 'LIVE_PRESENTATION' | 'RESULT';
  value: number;
  label: string;
  relativeValue: number;
  colors?: {
    ratingPollsChartColor: string;
    ratingPollsHeadingColor: string;
    ratingPollsLabelColor: string;
  };
  index: number;
  settings: Partial<InteractionSettings>;
  audienceContributionCount: number;
  audienceProfile: string[];
}) => {
  const displayStyle = props?.settings?.DisplayStyle || 'percentage';
  const iconStatus = props?.settings?.scoreLabelType === 'ICONS' ? props?.settings?.iconType : undefined;

  const styles = {
    EDIT_PRESENTATION: {
      representation: 'text-white text-sm font-semibold mt-2 ',
      bar: 'flex-1 min-w-0  max-w-[100px]',
      barRadius: 'rounded-[24px]',
      fontSize: '20px',
      profileSize: 'sm' as 'sm' | 'md' | 'xl',
      profilePosition: 'top-4',
      scale: props?.settings?.ratingPollScale === '5' ? 'scale-80' : 'scale-75',
      gap: props?.settings?.ratingPollScale === '5' ? 'gap-10' : 'gap-2',
    },
    LIVE_PRESENTATION: {
      representation: 'text-white text-xl font-semibold mt-2',
      bar: 'flex-1 min-w-0  max-w-[180px]',
      barRadius: 'rounded-[32px]',
      fontSize: '30px',
      profileSize: 'xl' as 'sm' | 'md' | 'xl',
      profilePosition: 'top-4',
      scale: props?.settings?.ratingPollScale === '5' ? 'scale-100' : 'scale-75',
      gap: props?.settings?.ratingPollScale === '5' ? 'gap-10' : 'gap-10'
    },
    RESULT: {
      representation: 'text-white text-xl font-semibold mt-2',
      bar: 'flex-1 min-w-0  max-w-[180px]',
      barRadius: 'rounded-[24px]',
      fontSize: '30px',
      profileSize: 'sm' as 'sm' | 'md' | 'xl',
      profilePosition: 'top-20',
      scale: props?.settings?.ratingPollScale === '5' ? 'scale-100' : 'scale-75',
      gap: props?.settings?.ratingPollScale === '5' ? 'gap-10' : 'gap-2'
    }
  };

  const getReprestation = () => {
    if (props?.settings?.scoreLabelType === 'ICONS') {
      if (props?.settings?.iconType === 'EMOJI') {
        const scale = props?.settings?.ratingPollScale as '5' | '10';
        return EMOJI_ICONS[scale][props.index + 1 as keyof typeof EMOJI_ICONS[typeof scale]];
      }

      if (props?.settings?.iconType === 'STAR') {
        return Array.from({ length: props.index + 1 }, (_, index) => <Star fontSize='inherit' className='text-[#FFE535] text-[12px] rotate-[36deg]' key={index} />);
      }
    }

    if (props?.settings?.scoreLabelType === 'LABELS') {
      if (props?.index === 0) {
        return props?.settings?.lowScoreLabel;
      }
      if (props?.settings?.ratingPollScale === '10') {
        return props?.index === 9 ? props?.settings?.highScoreLabel : '';
      }
      if (props?.settings?.ratingPollScale === '5') {
        return props?.index === 4 ? props?.settings?.highScoreLabel : '';
      }

      return props.index + 1;
    };
  };

  const Represtation = () => {
    const text = getReprestation() || '';

    return <p className={`text-white  max-w-full  px-2 ${iconStatus === 'STAR' ? 'rotate-180' : ''} ${styles[props.origin as keyof typeof styles]?.representation} break-words  `} style={{
      fontWeight: 'semibold',
      textAlign: 'center',
      color: colors?.ratingPollsLabelColor || '#FFFFFF',
    }}>{text}</p>;
  };

  const { colors } = props;

  const getColorWithOpacity = (hexColor: string, opacity = 0.5) => {
    if (hexColor.includes('rgba')) return hexColor;
    if (!hexColor) return `rgba(0, 0, 0, ${opacity})`;

    if (hexColor.charAt(0) === '#') {
      hexColor = hexColor.slice(1);
    }

    const isShortHex = hexColor.length === 3;

    const r = parseInt(isShortHex ? hexColor[0] + hexColor[0] : hexColor.slice(0, 2), 16);
    const g = parseInt(isShortHex ? hexColor[1] + hexColor[1] : hexColor.slice(2, 4), 16);
    const b = parseInt(isShortHex ? hexColor[2] + hexColor[2] : hexColor.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };
  const { height } = useSpring({
    from: { height: `${0}%` },
    to: { height: `${props?.relativeValue}%` },
    config: {
      duration: 1000,
      tension: 120,
      friction: 14,
      delay: props.index * 100,
      easing: t => t * t * (3 - 2 * t)
    }
  });

  return <div className={`w-full h-full items-center  flex z-0  flex-col ${styles[props.origin as keyof typeof styles]?.bar}`} >
    <div className={`w-full h-full  py-6 overflow-hidden flex flex-col    relative ${styles[props.origin as keyof typeof styles]?.barRadius}`} style={{
      backgroundColor: getColorWithOpacity(colors?.ratingPollsChartColor || '#E8634D', 0.5),
    }}>

      <div className={'w-full  flex flex-col items-center gap-4  '}>

        <Counter value={displayStyle === 'percentage' ? props.value : props.audienceContributionCount} suffix={displayStyle === 'percentage' ? '%' : ''} color={colors?.ratingPollsLabelColor || '#FFFFFF'} fontSize={styles[props.origin as keyof typeof styles]?.fontSize} />
        {props?.settings?.ratingPollShowProfilePictures && <div >
          <div className={`  ${styles[props.origin as keyof typeof styles]?.scale}`}>
            <GroupProfile usernames={props?.audienceProfile} colorPalette={colorPalette} contribution={props?.audienceProfile?.length || 0} />
          </div>
        </div>}

      </div>
      <animated.div className='w-full h-full flex flex-col  justify-end items-center absolute bottom-0 -z-40 ' style = {{
        height,
        backgroundColor: colors?.ratingPollsChartColor || '#E8634D'
      }}/>
      <div className='w-full flex justify-center items-center  mt-auto'>
        <Represtation />
      </div>
    </div>
    <p className='text-sm md:text-base lg:text-lg font-medium mt-4 z-50  ' style ={{
      color: colors?.ratingPollsLabelColor || '#FFFFFF',
      fontSize: styles[props.origin as keyof typeof styles]?.fontSize
    }}>{props?.label}</p>
  </div>;
};

export default Bar;
